/**
 * Custom hook for Resultatrapport data fetching and processing.
 * Extracts all state, API calls, and data transformation from the component.
 *
 * Data processing helpers are defined at module level (outside the hook)
 * to avoid recreating them on every render.
 */

import { useState, useEffect } from 'react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { findAccountSection, getStandardIncomeStatementStructure } from './resultReportUtils';

export default function useResultReport(companyId) {
    const [fiscalYears, setFiscalYears] = useState([]);
    const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [processedData, setProcessedData] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [yearPeriodOptions, setYearPeriodOptions] = useState([]);
    const [showAllYears, setShowAllYears] = useState(false);
    const [showVerifications, setShowVerifications] = useState(false);
    const [subtotals, setSubtotals] = useState({});
    const [metadata, setMetadata] = useState({});

    // Fetch fiscal years
    useEffect(() => { getFiscalYears(); }, [companyId]);

    const getFiscalYears = async () => {
        try {
            const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
            setFiscalYears(result.data.fiscalYears);
            if (result.data.fiscalYears.length > 0) {
                const latestFiscalYear = result.data.fiscalYears.sort((a, b) => b.year - a.year)[0];
                setSelectedFiscalYear(latestFiscalYear);
            }
        } catch (err) {
            setError('Failed to fetch fiscal years');
            setFiscalYears([]);
        } finally {
            setLoading(false);
        }
    };

    // Build period dropdown options
    useEffect(() => {
        if (!fiscalYears || fiscalYears.length === 0) return;

        const monthFormatter = new Intl.DateTimeFormat('sv-SE', { month: 'long' });
        const toLocalYMD = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        const sorted = [...fiscalYears].sort((a, b) => b.year - a.year);
        const limited = showAllYears ? sorted : sorted.slice(0, 5);

        const groups = limited.map((fy) => {
            const fyStart = new Date(fy.start_date);
            const fyEnd = new Date(fy.end_date);
            const opts = [];
            opts.push({
                value: { fiscalYear: fy, period: { type: 'year', startDate: fy.start_date, endDate: fy.end_date } },
                label: 'Hela året',
            });

            let cursor = new Date(fyStart.getFullYear(), fyStart.getMonth(), 1);
            while (cursor <= fyEnd) {
                const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
                const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
                const start = monthStart < fyStart ? fyStart : monthStart;
                const end = monthEnd > fyEnd ? fyEnd : monthEnd;
                const monthName = monthFormatter.format(monthStart);
                const label = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${monthStart.getFullYear()}`;
                opts.push({
                    value: { fiscalYear: fy, period: { type: 'month', startDate: toLocalYMD(start), endDate: toLocalYMD(end) } },
                    label,
                });
                cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
            }
            return { label: String(fy.year), options: opts };
        });

        const hasMore = !showAllYears && sorted.length > limited.length;
        const moreGroup = hasMore ? [{ label: '', options: [{ value: { action: '__more__' }, label: 'Visa fler år…' }] }] : [];
        setYearPeriodOptions([...groups, ...moreGroup]);

        const latestGroup = groups[0];
        if (latestGroup && latestGroup.options && latestGroup.options.length > 0) {
            const def = latestGroup.options[0];
            setSelectedFiscalYear(def.value.fiscalYear);
            setSelectedPeriod({ value: def.value.period, label: def.label });
        }
    }, [fiscalYears, showAllYears]);

    // Fetch report when selection changes
    useEffect(() => {
        if (selectedFiscalYear && selectedPeriod) getResultReport();
    }, [selectedFiscalYear, selectedPeriod, showVerifications]);

    const getResultReport = async () => {
        if (!selectedFiscalYear || !selectedPeriod) return;
        setLoading(true);
        try {
            const isWholeYear = selectedPeriod.value?.type === 'year';
            const params = {
                fiscalYearId: selectedFiscalYear.fiscal_year_id,
                includeComparison: isWholeYear ? 'true' : 'false',
                includeVerifications: showVerifications ? 'true' : 'false',
            };
            if (!isWholeYear) {
                params.startDate = selectedPeriod.value.startDate;
                params.endDate = selectedPeriod.value.endDate;
            }

            const response = await axios.get(getApiUrl(`${companyId}/reporting/result`), {
                params, withCredentials: true,
            });

            let dataMap;
            if (isWholeYear) {
                dataMap = response.data.data;
            } else {
                dataMap = {
                    [selectedFiscalYear.year]: {
                        fiscalYear: selectedFiscalYear,
                        data: response.data,
                    },
                };
            }

            setReportData(dataMap);

            // Extract subtotals and metadata per year
            const yearSubtotals = {};
            const yearMetadata = {};
            Object.entries(dataMap).forEach(([year, yearEntry]) => {
                const data = yearEntry.data || yearEntry;
                if (data._subtotals) yearSubtotals[year] = data._subtotals;
                if (data._metadata) yearMetadata[year] = data._metadata;
            });
            setSubtotals(yearSubtotals);
            setMetadata(yearMetadata);

            const processed = processMultiYearData(dataMap);
            setProcessedData(processed);
            setError(null);
        } catch (err) {
            setError('Failed to fetch result report');
            setReportData(null);
            setProcessedData(null);
        } finally {
            setLoading(false);
        }
    };

    // Event handlers (must be inside hook — they use setState)
    const handleYearPeriodChange = (opt) => {
        if (!opt || !opt.value) return;
        if (opt.value.action === '__more__') { setShowAllYears(true); return; }
        setSelectedFiscalYear(opt.value.fiscalYear);
        setSelectedPeriod({ value: opt.value.period, label: opt.label });
    };

    const refreshReport = () => {
        if (selectedFiscalYear && selectedPeriod) getResultReport();
    };

    return {
        fiscalYears, selectedFiscalYear, selectedPeriod, yearPeriodOptions,
        loading, error, reportData, processedData, subtotals, metadata, showVerifications,
        handleYearPeriodChange, setShowVerifications, setError, refreshReport,
    };
}

// ---------------------------------------------------------------------------
// Pure data processing functions (module-level, not recreated per render)
// ---------------------------------------------------------------------------

function processMultiYearData(data) {
    if (!data || !Object.keys(data).length) return null;

    const allYearsSortedAsc = Object.keys(data).map(y => parseInt(y, 10)).sort((a, b) => a - b);
    const availableYears = allYearsSortedAsc.slice(-3).reverse().map(String);

    const standardStructure = getStandardIncomeStatementStructure();
    const allAccounts = collectAllAccounts(data);
    const structureWithAccounts = assignAccountsToStructure(standardStructure, allAccounts);

    const processedYearData = {};
    availableYears.forEach(year => {
        processedYearData[year] = createYearData(structureWithAccounts, data[year]);
    });

    return {
        years: availableYears,
        data: processedYearData,
        originalData: data,
        allAccounts,
        structure: structureWithAccounts,
    };
}

function collectAllAccounts(data) {
    const allAccounts = new Map();
    Object.values(data).forEach(yearData => {
        const d = yearData?.data || yearData;
        if (d) collectAccountsRecursively(d, allAccounts);
    });
    return allAccounts;
}

function collectAccountsRecursively(obj, accountsMap) {
    if (!obj || typeof obj !== 'object') return;
    Object.values(obj).forEach(value => {
        if (value && typeof value === 'object') {
            if (value.accounts && Array.isArray(value.accounts)) {
                value.accounts.forEach(account => {
                    accountsMap.set(account.account_id, {
                        account_id: account.account_id,
                        account_number: account.account_number,
                        account_name: account.account_name,
                    });
                });
            } else {
                collectAccountsRecursively(value, accountsMap);
            }
        }
    });
}


function assignAccountsToStructure(structure, allAccounts) {
    const newStructure = JSON.parse(JSON.stringify(structure));

    allAccounts.forEach((account) => {
        const sectionPath = findAccountSection(account.account_number, structure);
        if (sectionPath) {
            let current = newStructure;
            for (const part of sectionPath) current = current[part];
            if (current && current.accounts) {
                current.accounts.push({
                    account_id: account.account_id,
                    account_number: account.account_number,
                    account_name: account.account_name,
                    opening_balance: '0.00', change: '0.00', closing_balance: '0.00',
                });
            }
        }
    });

    sortAccountsInStructure(newStructure);
    return newStructure;
}

function sortAccountsInStructure(structure) {
    Object.values(structure).forEach(value => {
        if (value && typeof value === 'object') {
            if (value.accounts && Array.isArray(value.accounts)) {
                value.accounts.sort((a, b) => parseInt(a.account_number) - parseInt(b.account_number));
            } else {
                sortAccountsInStructure(value);
            }
        }
    });
}

function createYearData(structure, yearData) {
    const yearStructure = JSON.parse(JSON.stringify(structure));
    if (!yearData || !yearData.data) return yearStructure;

    const accountValues = new Map();
    collectAccountValuesRecursively(yearData.data, accountValues);
    updateStructureWithValues(yearStructure, accountValues);
    recalculateAllTotals(yearStructure);
    return yearStructure;
}

function collectAccountValuesRecursively(obj, valuesMap) {
    if (!obj || typeof obj !== 'object') return;
    Object.values(obj).forEach(value => {
        if (value && typeof value === 'object') {
            if (value.accounts && Array.isArray(value.accounts)) {
                value.accounts.forEach(account => {
                    const data = {
                        opening_balance: account.opening_balance || '0.00',
                        change: account.change || '0.00',
                        closing_balance: account.closing_balance || '0.00',
                        account_number: account.account_number,
                    };
                    if (account.verifications) data.verifications = account.verifications;
                    valuesMap.set(account.account_id, data);
                    valuesMap.set(String(account.account_id), data);
                    valuesMap.set(Number(account.account_id), data);
                    if (account.account_number) valuesMap.set(account.account_number, data);
                });
            } else {
                collectAccountValuesRecursively(value, valuesMap);
            }
        }
    });
}

function updateStructureWithValues(structure, valuesMap) {
    Object.values(structure).forEach(value => {
        if (value && typeof value === 'object') {
            if (value.accounts && Array.isArray(value.accounts)) {
                value.accounts.forEach(account => {
                    let av = valuesMap.get(account.account_id)
                        || valuesMap.get(String(account.account_id))
                        || valuesMap.get(Number(account.account_id));
                    if (!av) {
                        for (const [, v] of valuesMap.entries()) {
                            if (v.account_number === account.account_number) { av = v; break; }
                        }
                    }
                    if (av) {
                        account.opening_balance = av.opening_balance;
                        account.change = av.change;
                        account.closing_balance = av.closing_balance;
                        if (av.verifications) account.verifications = av.verifications;
                    }
                });
            } else {
                updateStructureWithValues(value, valuesMap);
            }
        }
    });
}

function recalculateAllTotals(structure) {
    Object.entries(structure).forEach(([key, value]) => {
        if (value && typeof value === 'object' && key !== 'accounts') {
            if (value.accounts && Array.isArray(value.accounts)) {
                value.total_change = value.accounts.reduce((sum, account) => {
                    const parsed = parseFloat(String(account.change || 0).replace(/,/g, ''));
                    return sum + (isNaN(parsed) ? 0 : parsed);
                }, 0);
            } else {
                recalculateAllTotals(value);
                value.total_change = Object.entries(value)
                    .filter(([k, v]) => k !== 'total_change' && k !== 'accountRanges' && k !== 'workingReportOnly' && v && typeof v === 'object')
                    .reduce((sum, [, v]) => sum + (parseFloat(v.total_change) || 0), 0);
            }
        }
    });
}
